import { useQuery } from '@tanstack/react-query';
import { Brain, AlertTriangle, TrendingUp, Sparkles } from 'lucide-react';

interface Overlap {
  stock: string;
  etfs: Array<{
    etf: string;
    etfName: string;
    weight: number;
    effectiveExposure: number;
  }>;
  totalOverlap: number;
}

interface InsightsData {
  insights: string;
  overlaps: Overlap[];
  diversificationScore: number;
  portfolioSummary: {
    totalValue: number;
    totalHoldings: number;
    etfCount: number;
    stockCount: number;
  };
}

async function getInsights(): Promise<InsightsData> {
  const response = await fetch('/api/insights');
  if (!response.ok) {
    throw new Error('Failed to fetch insights');
  }
  return response.json();
}

export default function PortfolioInsights() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['insights'],
    queryFn: getInsights,
    refetchInterval: 60 * 1000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <Brain className="w-6 h-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">AI Portfolio Insights</h3>
        </div>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <Brain className="w-6 h-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">AI Portfolio Insights</h3>
        </div>
        <p className="text-gray-500">Unable to load insights. Please try again later.</p>
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
            <Brain className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">AI Portfolio Insights</h3>
            <p className="text-sm text-gray-500">Powered by Cloudflare Workers AI</p>
          </div>
        </div>
        
        {/* Diversification Score */}
        <div className="text-center">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold ${getScoreColor(data.diversificationScore)}`}>
            <TrendingUp className="w-5 h-5" />
            <span className="text-2xl">{data.diversificationScore}</span>
            <span className="text-sm">/100</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">{getScoreLabel(data.diversificationScore)}</p>
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
          <p className="text-gray-800 leading-relaxed">{data.insights}</p>
        </div>
      </div>

      {/* Overlaps */}
      {data.overlaps && data.overlaps.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span>Hidden Overlaps Detected</span>
          </div>
          
          {data.overlaps.map((overlap) => (
            <div key={overlap.stock} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-gray-900">{overlap.stock}</h4>
                  <p className="text-sm text-gray-600">
                    Total effective exposure: <span className="font-medium text-amber-700">{overlap.totalOverlap.toFixed(1)}%</span>
                  </p>
                </div>
              </div>
              
              <div className="space-y-2 mt-3">
                {overlap.etfs.map((etf) => (
                  <div key={etf.etf} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                    <div>
                      <span className="font-medium text-gray-900">{etf.etf}</span>
                      <span className="text-gray-500 ml-2">({etf.etfName})</span>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-600">{etf.weight}% of ETF</span>
                      <span className="text-amber-700 font-medium ml-2">
                        → {etf.effectiveExposure.toFixed(1)}% exposure
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {data.portfolioSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{data.portfolioSummary.totalHoldings}</p>
            <p className="text-xs text-gray-600">Total Holdings</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{data.portfolioSummary.etfCount}</p>
            <p className="text-xs text-gray-600">ETFs</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{data.portfolioSummary.stockCount}</p>
            <p className="text-xs text-gray-600">Individual Stocks</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              ${(data.portfolioSummary.totalValue / 1000).toFixed(1)}k
            </p>
            <p className="text-xs text-gray-600">Total Value</p>
          </div>
        </div>
      )}
    </div>
  );
}
